import { useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { timsurveyApi, tecniciApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { TimsurveyPraticaDettaglio } from '../../types/timsurvey'

const RUOLI_BACKOFFICE = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico']
const RUOLI_TECNICO = ['tecnico', 'tecnico_senior']

interface TecnicoOption {
  id: number
  nome: string
  cognome: string
}

function fmtData(d: string | null) {
  if (!d) return '—'
  try { return new Date(d).toLocaleString('it-IT') } catch { return d }
}

export default function TimsurveyDettaglio() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [assegnaAperto, setAssegnaAperto] = useState(false)
  const [tecnicoSelezionato, setTecnicoSelezionato] = useState('')

  const isBackoffice = !!user && RUOLI_BACKOFFICE.includes(user.role)
  const isTecnico = !!user && RUOLI_TECNICO.includes(user.role)

  const { data: pratica, isLoading } = useQuery({
    queryKey: ['timsurvey-pratica', id],
    queryFn: () => timsurveyApi.pratiche.get(id!).then(r => r.data as TimsurveyPraticaDettaglio),
    enabled: !!id,
  })

  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-per-assegna'],
    queryFn: () => tecniciApi.list({ page_size: 500 }).then(r => r.data),
    enabled: isBackoffice,
  })
  const tecnici: TecnicoOption[] = tecniciData?.items ?? []

  if (isTecnico && pratica) {
    return <Navigate to={`/timsurvey/${id}/wizard`} replace />
  }

  if (isLoading || !pratica) {
    return <div className="card p-8 text-center text-sm text-slate-400">Caricamento...</div>
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['timsurvey-pratica', id] })

  const assegna = async () => {
    if (!tecnicoSelezionato) return
    try {
      await timsurveyApi.pratiche.assegna(id!, Number(tecnicoSelezionato))
      toast.success('Tecnico assegnato')
      setAssegnaAperto(false)
      invalidate()
    } catch (err: any) {
      toast.error(err.response?.data?.detail ?? 'Errore assegnazione')
    }
  }

  const annulla = async () => {
    if (!window.confirm('Annullare questa pratica?')) return
    try {
      await timsurveyApi.pratiche.annulla(id!)
      toast.success('Pratica annullata')
      invalidate()
    } catch (err: any) {
      toast.error(err.response?.data?.detail ?? 'Errore annullamento')
    }
  }

  const chiudi = async () => {
    if (!window.confirm('Chiudere la pratica e generare il PDF definitivo?')) return
    try {
      await timsurveyApi.pratiche.chiudi(id!)
      toast.success('Pratica chiusa')
      invalidate()
    } catch (err: any) {
      toast.error(err.response?.data?.detail ?? 'Errore chiusura')
    }
  }

  const scaricaPdf = async () => {
    try {
      const res = await timsurveyApi.pratiche.pdf(id!)
      const url = URL.createObjectURL(res.data as Blob)
      window.open(url, '_blank')
    } catch {
      toast.error('Errore generazione PDF')
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">{pratica.codice}</h1>
          <p className="page-subtitle">{pratica.ragione_sociale}</p>
        </div>
        <span className="badge text-xs px-2 py-0.5 rounded-full bg-slate-100">{pratica.stato.replace(/_/g, ' ')}</span>
      </div>

      <div className="card p-4 grid grid-cols-2 gap-3 text-sm">
        <div><span className="text-slate-500">Indirizzo:</span> {pratica.indirizzo ?? '—'}</div>
        <div><span className="text-slate-500">Città:</span> {pratica.citta ?? '—'} {pratica.provincia ?? ''} {pratica.cap ?? ''}</div>
        <div><span className="text-slate-500">Referente cliente:</span> {pratica.referente_cliente ?? '—'}</div>
        <div><span className="text-slate-500">Referente OA:</span> {pratica.referente_oa ?? '—'}</div>
        <div><span className="text-slate-500">Referente vendita:</span> {pratica.referente_vendita ?? '—'}</div>
        <div><span className="text-slate-500">ID GRS:</span> {pratica.id_grs_site_survey ?? '—'}</div>
        <div><span className="text-slate-500">N° ODA:</span> {pratica.n_oda_sopralluogo ?? '—'}</div>
        <div><span className="text-slate-500">Data sopralluogo prevista:</span> {fmtData(pratica.data_sopralluogo_prevista)}</div>
        <div><span className="text-slate-500">Tecnico assegnato:</span> {(() => {
          if (!pratica.tecnico_id) return '—'
          const t = tecnici.find(t => t.id === pratica.tecnico_id)
          return t ? `${t.nome} ${t.cognome}` : pratica.tecnico_id
        })()}</div>
      </div>

      {pratica.verbale && (
        <div className="card p-4 space-y-2 text-sm">
          <div className="font-semibold text-slate-700">Verbale</div>
          <div><span className="text-slate-500">Esito:</span> {pratica.verbale.esito ?? '—'}</div>
          <div><span className="text-slate-500">Situazione:</span> {pratica.verbale.situazione ?? '—'}</div>
          <div><span className="text-slate-500">Scenario firma:</span> {pratica.verbale.scenario_firma ?? '—'}</div>
          <div><span className="text-slate-500">Compilato il:</span> {fmtData(pratica.verbale.compilato_at)}</div>
          <div><span className="text-slate-500">Firmato il:</span> {fmtData(pratica.verbale.firmato_at)}</div>
        </div>
      )}

      <div className="card p-4">
        <div className="font-semibold text-slate-700 mb-3">Storico stati</div>
        <div className="space-y-2 text-sm">
          {pratica.storico_stati.map((s, i) => (
            <div key={i} className="flex justify-between border-b border-slate-50 pb-2">
              <span className="font-medium">{s.stato}</span>
              <span className="text-slate-500">{fmtData(s.timestamp)} — {s.utente}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {isBackoffice && pratica.stato === 'CREATA' && (
          <button className="btn-secondary" onClick={() => setAssegnaAperto(true)}>Assegna tecnico</button>
        )}
        {isBackoffice && pratica.stato === 'FIRMATA' && (
          <button className="btn-primary" onClick={chiudi}>Chiudi pratica</button>
        )}
        {isBackoffice && pratica.stato !== 'CHIUSA' && pratica.stato !== 'ANNULLATA' && (
          <button className="btn-secondary" onClick={annulla}>Annulla pratica</button>
        )}
        <button className="btn-secondary" onClick={scaricaPdf}>Scarica PDF</button>
        <button className="btn-secondary" onClick={() => navigate('/timsurvey')}>Torna alla lista</button>
      </div>

      {assegnaAperto && (
        <div className="card p-4 space-y-3 max-w-sm">
          <div className="font-semibold text-slate-700">Assegna a tecnico</div>
          <select className="select w-full" value={tecnicoSelezionato} onChange={e => setTecnicoSelezionato(e.target.value)}>
            <option value="">Seleziona tecnico...</option>
            {tecnici.map(t => (
              <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button className="btn-primary" disabled={!tecnicoSelezionato} onClick={assegna}>Conferma</button>
            <button className="btn-secondary" onClick={() => setAssegnaAperto(false)}>Annulla</button>
          </div>
        </div>
      )}
    </div>
  )
}
